/** Fixed yard floodlight; the room owns its actual light and any storm flash. */
import * as THREE from 'three';
import { surface } from '../materials';
import type { AssetDefinition } from '../types';

function part(geometry: THREE.BufferGeometry, material: THREE.Material, name: string): THREE.Mesh {
  const result = new THREE.Mesh(geometry, material);
  result.name = name;
  result.castShadow = true;
  result.receiveShadow = true;
  return result;
}

/**
 * How far the pole stands back from the asset's origin, in metres.
 *
 * Everything below is built with the pole at local Z 0, which is the only way
 * to write a cowled head that reaches out along +Z and stay able to reason
 * about it. But the head really does reach out: measured, the mesh runs Z
 * −0.427…+0.793, so its centre is 0.183 forward of the pole, and `asset.spec.ts`
 * requires the bounding box to be centred within 0.03 m so that a placement is
 * `position.set(x, 0, z)` with no fudge factor.
 *
 * A `stand` group shifted back by that amount makes the origin the bounding-box
 * centre without moving one vertex relative to another. **The consequence is
 * worth knowing before you place one:** the origin is NOT the foot of the pole,
 * it is 0.183 m in front of it. Ask the group for `stand` if you need the pole.
 */
const HEAD_REACH = 0.1833;

function buildYardFloodlight(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'yard-floodlight';

  const stand = new THREE.Group();
  stand.name = 'stand';
  stand.position.set(0, 0, -HEAD_REACH);
  group.add(stand);

  const steel = surface('metal', 0x8a9096);
  const darkSteel = surface('metal', 0x3c4247);

  const base = new THREE.Group();
  base.name = 'base-plate-and-lugs';
  const plate = part(new THREE.BoxGeometry(0.42, 0.03, 0.42), steel, 'base-plate');
  plate.position.set(0, 0.015, 0);
  base.add(plate);
  const lugGeometry = new THREE.BoxGeometry(0.15, 0.03, 0.10);
  const boltGeometry = new THREE.CylinderGeometry(0.025, 0.025, 0.02, 8);
  for (const [x, z, name] of [[-0.30, -0.375, 'rear-left'], [0.30, -0.375, 'rear-right'], [-0.30, 0.375, 'front-left'], [0.30, 0.375, 'front-right']] as const) {
    const lug = part(lugGeometry, darkSteel, `bolt-lug-${name}`);
    lug.position.set(x, 0.015, z);
    const bolt = part(boltGeometry, steel, `anchor-bolt-${name}`);
    bolt.position.set(x, 0.04, z);
    base.add(lug, bolt);
  }
  stand.add(base);

  const pole = part(new THREE.CylinderGeometry(0.05, 0.05, 4.60, 12), steel, 'twelve-sided-pole');
  pole.position.set(0, 2.33, 0);
  stand.add(pole);

  const bracket = new THREE.Group();
  bracket.name = 'forward-bracket';
  const arm = part(new THREE.BoxGeometry(0.06, 0.06, 0.45), steel, 'bracket-arm');
  arm.position.set(0, 4.55, 0.225);
  bracket.add(arm);
  stand.add(bracket);

  const head = new THREE.Group();
  head.name = 'pitched-floodlight-head';
  const pitch = THREE.MathUtils.degToRad(35);
  head.position.set(0, 4.55, 0.45);
  head.rotation.x = pitch;
  const housing = part(new THREE.BoxGeometry(0.62, 0.30, 0.42), darkSteel, 'floodlight-housing');
  head.add(housing);
  const lipTop = part(new THREE.BoxGeometry(0.74, 0.06, 0.06), darkSteel, 'cowl-top-lip');
  lipTop.position.set(0, 0.18, 0.24);
  const lipSideGeometry = new THREE.BoxGeometry(0.06, 0.30, 0.06);
  for (const [x, name] of [[-0.34, 'left'], [0.34, 'right']] as const) {
    const side = part(lipSideGeometry, darkSteel, `cowl-${name}-lip`);
    side.position.set(x, 0, 0.24);
    head.add(side);
  }
  head.add(lipTop);

  // The aperture is one housing half-depth along local +Z. Applying the head's
  // +35-degree X pitch to the arm end gives root coordinates
  // (0, 4.55 - 0.21 sin(35), 0.45 + 0.21 cos(35)); keeping the empty group in
  // root space prevents a room-attached light from being pitched a second time.
  const emitter = new THREE.Group();
  emitter.name = 'lamp-emitter';
  emitter.position.set(0, 4.55 - 0.21 * Math.sin(pitch), 0.45 + 0.21 * Math.cos(pitch));
  stand.add(head);
  stand.add(emitter);

  const conduit = new THREE.Group();
  conduit.name = 'rear-conduit';
  const cable = part(new THREE.CylinderGeometry(0.01, 0.01, 4.40, 6), darkSteel, 'conduit-down-back');
  cable.position.set(0, 2.28, -0.06);
  conduit.add(cable);
  const clipGeometry = new THREE.TorusGeometry(0.062, 0.01, 4, 8);
  for (const [y, name] of [[1.30, 'lower'], [3.30, 'upper']] as const) {
    const clip = part(clipGeometry, steel, `conduit-p-clip-${name}`);
    clip.rotation.x = Math.PI / 2;
    clip.position.set(0, y, -0.06);
    conduit.add(clip);
  }
  stand.add(conduit);

  return group;
}

export const yardFloodlightAsset: AssetDefinition = {
  id: 'yard-floodlight',
  label: 'Yard floodlight',
  description: 'A fixed, forward-facing steel floodlight for the loading yard.',
  // Measured, not intended: the cowled head reaches 0.79 m out along +Z and
  // the pole tops out at 4.79 m, not 5. `footprint` drives the preview's camera
  // framing and the contract check, so it describes the mesh that exists.
  footprint: { width: 0.75, height: 4.79, length: 1.22 },
  create: buildYardFloodlight,
};
